import { useContext, useState } from 'react'

import { Box, Button, DragNDrop, Icon, Text } from '@fairfashion/design-system'
import { useTheme } from 'styled-components'

import { BackButton } from '../../../../components/BackButton/back-button.comp'
import { ConfirmModalContext } from '../../../../components/ConfirmModal'
import { GlobalDataContext } from '../../../../contexts/globalData'
import { createItemTraceabilityTemplateSheet } from '../../../../utilities/files/create-item-traceability-template-sheet'
import { OriginItemsContext } from '../../origin-items.context'

import * as Styles from './upload-sheet.styles'

export const UploadSheet = () => {
  const { actions } = useContext(OriginItemsContext)
  const [file, setFile] = useState<File>()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const { companySegmentType } = useContext(GlobalDataContext)
  const { confirmModal } = useContext(ConfirmModalContext)

  const theme = useTheme()

  const handleOnChange = (_files?: File[]) => {
    setError(false)

    setFile(_files?.[0])
  }

  const handleDownloadTemplate = () => {
    const segments = Object.values(companySegmentType)
      .map((s) => s.translate)
      .join(',')

    createItemTraceabilityTemplateSheet(segments)
  }

  const handleSubmit = async () => {
    if (
      file &&
      (await confirmModal(
        'Enviar planilha',
        'Ao enviar a planilha, qualquer dado previamente inserido manualmente pela interface será substituído. Após o envio, não será possível alterar os dados de origem cadastrados para este item. Certifique-se de que todas as informações na planilha estão corretas antes de confirmar o envio.',
      ))
    ) {
      try {
        setLoading(true)
        await actions.handleSubmitSheet(file)
      } catch (err) {
        setError(true)
        console.log(err)
      } finally {
        setLoading(false)
      }
    }
  }

  return (
    <Box width="100%" height="100%" position="relative" direction="column">
      <Box width="100%" padding={3} direction="column" flex={1} gap={3}>
        <Box gap={1} alignItems="center">
          <BackButton onClick={() => actions.setTab('initial')} />
          <Text color="grey_3" size="small">
            Atenção: certifique-se de salvar antes de sair, caso contrário seus
            dados serão perdidos.
          </Text>
        </Box>
        <Box
          width="100%"
          justifyContent="center"
          alignItems="center"
          direction="column"
        >
          <Box width="1000px" maxWidth="100%">
            <Box
              padding={3}
              border={{ width: 1, color: 'grey_5' }}
              boxShadow={theme.box_shadow.card}
              backgroundColor="white"
              borderRadius="16px"
              width="100%"
              direction="column"
              gap={3}
            >
              <Text size="large" fontWeight="bold">
                <Box gap={1} alignItems="center">
                  <Icon name="information" size={24} color="secondary" />{' '}
                  Instruções
                </Box>
              </Text>

              <Box gap={1} direction="column" width="100%">
                <Text width="100%">
                  {/* <Styles.StyledPre> */}
                  1. Faça o
                  <Styles.StyledLink onClick={handleDownloadTemplate}>
                    {' '}
                    download{' '}
                    <Icon name="download" color="secondary" size={16} />{' '}
                  </Styles.StyledLink>
                  do template de Excel fornecido. Ele contém todas as colunas
                  necessárias para o preenchimento adequado.
                  {/* </Styles.StyledPre> */}
                </Text>
                <Text>2. Preencha os Dados no Excel:</Text>
                <Styles.StyledUl>
                  <li>
                    <Text>
                      <strong>CNPJ ou CPF</strong>: Insira apenas números, sem
                      formatação.
                    </Text>
                  </li>
                  <li>
                    <Text>
                      <strong>Nome do Fornecedor</strong>,{' '}
                      <strong>Segmento</strong> e <strong>CPF/CNPJ</strong> são
                      obrigatórios.
                    </Text>
                  </li>

                  <li>
                    <Text>
                      Dados do Item e Nota Fiscal são opcionais, mas
                      recomendados para uma rastreabilidade mais completa.
                    </Text>
                  </li>
                  <li>
                    <Text>
                      <strong>Data</strong>: Utilize o formato{' '}
                      <code>dd/mm/aa</code>
                    </Text>
                  </li>
                  <li>
                    <Text>
                      Se for desejado enviar mais de uma etapa de
                      rastreabilidade (etapas verticalizadas), repita a
                      estrutura inicial do template.
                    </Text>
                  </li>
                </Styles.StyledUl>
                <Text>
                  3. Após preencher, faça o upload do arquivo Excel abaixo.
                </Text>
                <Text>
                  4. Clique em “Salvar” para enviar o arquivo. Os dados serão
                  processados e em breve estarão disponíveis na aba de
                  Auditoria.
                </Text>

                <Box marginTop={3}>
                  <Text size="small" fontWeight="bold">
                    Importante: Após salvar, os dados de origem do item não
                    poderão ser alterados. Revise bem as informações antes de
                    confirmar. Ao escolher a opção de planilha, o que for
                    inserido via interface será ignorado.
                  </Text>
                </Box>
              </Box>

              <DragNDrop
                onChange={handleOnChange}
                fileType=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                fileTypeError="Tipo de arquivo inválido"
              />
            </Box>
          </Box>

          {error && (
            <Box width="100%" justifyContent="flex-end" marginTop={3}>
              <Text color="error">
                Não foi possível enviar a planilha, verifique se os dados estão
                corretos.
              </Text>
            </Box>
          )}

          <Box
            gap={2}
            width="100%"
            alignItems="center"
            justifyContent="flex-end"
            paddingY={3}
          >
            <Box gap={2}>
              <Button
                variant="outlined"
                onClick={() => actions.setTab('initial')}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                onClick={handleSubmit}
                disabled={!file}
                loading={loading}
                width="130px"
              >
                Salvar
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
